import { api, ApiError } from './api.js'
import { db } from './db.js'
import { mergePendingOperation, type QueueOperation } from './sync-model.js'

type Listener = (event: { entityKey: string; state: string; error?: string; server?: unknown }) => void
const listeners = new Set<Listener>()
let running = false
export const onSync = (listener: Listener) => { listeners.add(listener); return () => listeners.delete(listener) }
const emit = (event: Parameters<Listener>[0]) => listeners.forEach((listener) => listener(event))

export async function persistDraft(entityKey: string, date: string, payload: Record<string, unknown>) {
  const now = Date.now()
  await db.drafts.put({ entityKey, date, payload, updatedAt: now, storageOk: true })
  emit({ entityKey, state: 'local' })
}

export async function queueDraft(entityKey: string, date: string, method: string, url: string, payload: Record<string, unknown>) {
  const now = Date.now()
  await db.transaction('rw', db.drafts, db.operations, async () => {
    await db.drafts.put({ entityKey, date, payload, updatedAt: now, storageOk: true })
    const existing = await db.operations.where('entityKey').equals(entityKey).filter((item) => item.state !== 'conflict').last()
    const operation: QueueOperation = existing
      ? { ...mergePendingOperation(existing, payload, now), method, url }
      : { operationId: crypto.randomUUID(), entityKey, state: 'pending', payload, method, url, createdAt: now, updatedAt: now, attempts: 0 }
    await db.operations.put(operation)
  })
  emit({ entityKey, state: 'local' })
  void flushQueue()
}

export async function persistAndQueue(entityKey: string, date: string, method: string, url: string, payload: Record<string, unknown>) {
  await persistDraft(entityKey, date, payload)
  await queueDraft(entityKey, date, method, url, payload)
}

export async function flushQueue() {
  if (running || !navigator.onLine) return
  running = true
  try {
    // A page reload can interrupt an in-flight request. Replay its original ID
    // so the server returns the committed result without applying it twice.
    const queued = await db.operations.where('state').anyOf('pending', 'failed', 'syncing').sortBy('createdAt')
    for (const operation of queued) {
      if (!operation.method || !operation.url) continue
      await db.operations.update(operation.operationId, { state: 'syncing', attempts: operation.attempts + 1 })
      emit({ entityKey: operation.entityKey, state: 'syncing' })
      try {
        const result = await api<Record<string, any>>(operation.url, { method: operation.method, operationId: operation.operationId, body: JSON.stringify(operation.payload) })
        await db.transaction('rw', db.operations, db.drafts, async () => {
          await db.operations.delete(operation.operationId)
          const newer = await db.operations.where('entityKey').equals(operation.entityKey).first()
          if (newer && result?.record?.version) await db.operations.update(newer.operationId, { payload: { ...newer.payload, expectedVersion: result.record.version } })
          if (!newer) await db.drafts.delete(operation.entityKey)
        })
        emit({ entityKey: operation.entityKey, state: 'synced' })
      } catch (error) {
        const apiError = error as ApiError
        if (apiError.status === 409) {
          await db.operations.update(operation.operationId, { state: 'conflict', error: apiError.message, serverValue: apiError.body?.server })
          emit({ entityKey: operation.entityKey, state: 'conflict', error: apiError.message, server: apiError.body?.server })
        } else {
          await db.operations.update(operation.operationId, { state: 'failed', error: apiError.message })
          emit({ entityKey: operation.entityKey, state: 'failed', error: apiError.message })
          if (apiError.status === 401) break
        }
      }
    }
  } finally { running = false }
}

export async function resolveConflict(operationId: string, choice: 'server' | 'local', latestVersion?: number) {
  const operation = await db.operations.get(operationId)
  if (!operation) return
  if (choice === 'server') {
    await db.transaction('rw', db.operations, db.drafts, async () => { await db.operations.delete(operationId); await db.drafts.delete(operation.entityKey) })
    emit({ entityKey: operation.entityKey, state: 'synced' })
    return
  }
  await db.operations.delete(operationId)
  await db.operations.put({ ...operation, operationId: crypto.randomUUID(), state: 'pending', payload: { ...operation.payload, expectedVersion: latestVersion }, serverValue: undefined, error: undefined, attempts: 0, updatedAt: Date.now() })
  void flushQueue()
}
